import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    const trimmed = cookie.trim();

    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }

  return null;
}

const getAccessToken = () => {
  return (
    localStorage.getItem("access") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
};

const Client = () => {
  const [tipo, setTipo] = useState("N");
  const [esCliente, setEsCliente] = useState(true);
  const [esProveedor, setEsProveedor] = useState(false);
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [consultandoSri, setConsultandoSri] = useState(false);
  const [form, setForm] = useState({
    cedula: "",
    ruc: "",
    razon_social: "",
    nombre_comercial: "",
    telefono: "",
    correo: "",
    direccion: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const navigate = useNavigate();

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const consultarSri = async () => {
    const identificacion = form.cedula.trim();

    if (!/^\d{10}$/.test(identificacion)) {
      showSnackbar("warning", "Ingrese una cédula válida de 10 dígitos.");
      return;
    }

    const token = getAccessToken();

    if (!token) {
      showSnackbar("error", "No se encontró el token de autenticación.");
      return;
    }

    setConsultandoSri(true);

    try {
      const res = await fetch("/clientes/consultar-sri/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ identificacion }),
      });

      const data = await res.json();

      if (!res.ok || !data?.encontrado) {
        showSnackbar(
          res.status === 404 ? "warning" : "error",
          data?.mensaje || data?.error || "No fue posible consultar los datos en el SRI."
        );
        return;
      }

      const persona = data.persona || {};

      setTipo(persona.tipo || "N");
      setForm((prev) => ({
        ...prev,
        cedula: persona.cedula || prev.cedula,
        ruc: persona.ruc || prev.ruc,
        razon_social: persona.razon_social || prev.razon_social,
        nombre_comercial:
          persona.nombre_comercial ||
          persona.razon_social ||
          prev.nombre_comercial,
      }));

      showSnackbar("success", "Datos encontrados en el SRI.");
    } catch {
      showSnackbar("error", "No se pudo conectar con el servidor.");
    } finally {
      setConsultandoSri(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const data = {
      tipo,
      razon_social: form.razon_social.toUpperCase().trim(),
      nombre_comercial: form.nombre_comercial.toUpperCase().trim(),
      cedula: form.cedula.trim(),
      ruc: form.ruc.trim(),
      telefono: form.telefono.trim(),
      correo: form.correo.trim(),
      direccion: form.direccion.trim(),
      es_cliente: esCliente,
      es_proveedor: esProveedor,
      es_empleado: false,
      es_extranjero: false,
    };

    try {
      const res = await fetch("/clientes/crear/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        setTipo("N");
        setEsCliente(true);
        setEsProveedor(false);
        setForm({
          cedula: "",
          ruc: "",
          razon_social: "",
          nombre_comercial: "",
          telefono: "",
          correo: "",
          direccion: "",
        });

        navigate("/admin-panel/clientes", {
          state: {
            snackbar: {
              type: "success",
              message: "Persona registrada correctamente.",
            },
          },
          replace: true,
        });
      } else {
        let message = "Error al guardar la persona.";

        try {
          const error = await res.json();

          if (typeof error?.error === "string") {
            message = error.error;
          } else if (error?.error && typeof error.error === "object") {
            const firstValue = Object.values(error.error)[0];
            message = Array.isArray(firstValue)
              ? firstValue[0]
              : String(firstValue);
          }
        } catch {}

        showSnackbar("error", message);
      }
    } catch {
      showSnackbar("error", "No se pudo conectar con el servidor.");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
      });
    } catch {}

    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
            >
              Regresar
            </Button>
          </Box>

          <Paper
            sx={{
              p: { xs: 2, sm: 3, md: 3.2 },
              maxWidth: 900,
              mx: "auto",
              mb: 6,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2.5,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Registrar persona
                </Typography>

                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Completa la información principal del cliente o proveedor
                </Typography>
              </Box>
            </Box>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Persona</InputLabel>

                    <Select
                      value={tipo}
                      label="Tipo de Persona"
                      onChange={(event) => setTipo(event.target.value)}
                    >
                      <MenuItem value="N">Natural</MenuItem>
                      <MenuItem value="J">Jurídica</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      minHeight: 40,
                      display: "flex",
                      alignItems: "center",
                      px: 1.2,
                      border: "1px solid #dbe3f0",
                      borderRadius: 2,
                      bgcolor: "#fff",
                    }}
                  >
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={esCliente}
                            onChange={(event) => setEsCliente(event.target.checked)}
                            sx={{ py: 0.4 }}
                          />
                        }
                        label="Cliente"
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={esProveedor}
                            onChange={(event) => setEsProveedor(event.target.checked)}
                            sx={{ py: 0.4 }}
                          />
                        }
                        label="Proveedor"
                      />
                    </FormGroup>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cédula"
                    name="cedula"
                    value={form.cedula}
                    onChange={(event) => {
                      const value = event.target.value.replace(/\D/g, "").slice(0, 10);

                      setForm((prev) => ({
                        ...prev,
                        cedula: value,
                      }));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        consultarSri();
                      }
                    }}
                    fullWidth
                    size="small"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 10,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={consultarSri}
                            disabled={consultandoSri}
                            edge="end"
                            title="Consultar en el SRI"
                          >
                            {consultandoSri
                              ? <CircularProgress size={20} />
                              : <SearchIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="RUC"
                    name="ruc"
                    value={form.ruc}
                    onChange={(event) => {
                      const value = event.target.value.replace(/\D/g, "").slice(0, 13);

                      setForm((prev) => ({
                        ...prev,
                        ruc: value,
                      }));
                    }}
                    fullWidth
                    size="small"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 13,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={tipo === "N" ? "Nombre completo" : "Razón Social"}
                    name="razon_social"
                    value={form.razon_social}
                    onChange={handleChange("razon_social")}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Nombre Comercial"
                    name="nombre_comercial"
                    value={form.nombre_comercial}
                    onChange={handleChange("nombre_comercial")}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Teléfono"
                    name="telefono"
                    value={form.telefono}
                    onChange={handleChange("telefono")}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Correo Electrónico"
                    name="correo"
                    type="email"
                    value={form.correo}
                    onChange={handleChange("correo")}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Dirección"
                    name="direccion"
                    value={form.direccion}
                    onChange={handleChange("direccion")}
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    bgcolor: "#1565C0",
                    "&:hover": { bgcolor: "#0D47A1" },
                    borderRadius: 2,
                    px: 2.4,
                    fontWeight: 700,
                    boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                  }}
                >
                  Guardar Persona
                </Button>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Client;